import React from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { motion } from "framer-motion";
import ourStoryImg from "../../Assets/AboutusSection/ourstoryleft.jpg";

const OurStorySection = () => {
  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FCF9F8",
        py: { xs: "64px", md: "128px" },
        display: "flex",
        justifyContent: "center",
        opacity: 1,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1536px",
          width: { xs: "100%", lg: "1280px" },
          px: { xs: "24px", md: "48px" },
        }}
      >
        <Grid container spacing={{ xs: 6, md: 12 }} alignItems="flex-start">
          {/* Left Side: Image with Overlaying Box */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "544px",
                height: { xs: "400px", md: "600px" },
                // Left aligned layout
                ml: 0,
                mr: "auto",
                mt: 0,
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                style={{ width: "100%", height: "100%" }}
              >
                <Box
                  component="img"
                  src={ourStoryImg}
                  alt="Our Story"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "4px",
                    boxShadow: "0px 20px 60px rgba(28, 27, 27, 0.15)",
                  }}
                />
              </motion.div>

              {/* Orange Box - Overlapping nicely */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{
                  position: "absolute",
                  bottom: "-30px",
                  right: "-40px", // Moved to the right side of the image for balance
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "160px", md: "213px" },
                    height: { xs: "140px", md: "180px" },
                    backgroundColor: "#FF5722",
                    borderRadius: "8px",
                    border: "1px solid #E2BFB51A",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#FFFFFF",
                    boxShadow: "0px 15px 40px rgba(255, 87, 34, 0.3)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 800,
                      fontSize: { xs: "40px", md: "56px" },
                      lineHeight: 1,
                    }}
                  >
                    8+
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "2px",
                      mt: 1,
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    YEARS OF CRAFT
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </Grid>

          {/* Right Side: Content - Strictly Left Aligned */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "544px",
                display: "flex",
                flexDirection: "column",
                gap: "32px",
                textAlign: "left", // Force left alignment
                mt: 0,
                pt: 0,
              }}
            >
              {/* Heading */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: "32px", md: "48px" },
                    lineHeight: "1.1",
                    letterSpacing: "-1.2px",
                    color: "#1C1B1B",
                    m: 0,
                    p: 0,
                    textAlign: "left", // Explicit left align
                  }}
                >
                  Crafting Spaces,
                  <br />
                  Defining Horizons
                </Typography>
              </motion.div>

              {/* Text Paragraphs */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  textAlign: "left", // Explicit left align
                }}
              >
                {[
                  "Founded on the principles of architectural integrity and uncompromising quality, Abilio Realty began as a boutique firm in Lisbon, dedicated to restoring historical estates to their former glory.",
                  "Over the years, we evolved into a multi-disciplinary powerhouse, bridging the gap between innovative construction, avant-garde interior design, and strategic real estate investment. Our growth has been fueled by a single obsession: the pursuit of the extraordinary.",
                  "Today, Abilio stands as a beacon for those who view property not just as an asset, but as a masterpiece to be lived in.",
                ].map((text, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 * index }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 300,
                        fontSize: "18px",
                        lineHeight: "29.25px",
                        color: "#5A413A",
                        letterSpacing: "0px",
                        textAlign: "left", // Explicit left align
                      }}
                    >
                      {text}
                    </Typography>
                  </motion.div>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OurStorySection;
